import React, { useState, useEffect, useCallback } from 'react';
import { Table, Button, Space, Popconfirm, message, Typography, Card, Tag } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { AdvClient } from '../../types/adv';
import { advClientService } from '../../services/advClientService';
import AdvClientForm from './AdvClientForm';

const { Title } = Typography;

const AdvClientsPage: React.FC = () => {
  const { t } = useTranslation();
  const [clients, setClients] = useState<AdvClient[]>([]);
  const [loading, setLoading] = useState(false);
  const [formVisible, setFormVisible] = useState(false);
  const [selectedClient, setSelectedClient] = useState<AdvClient | undefined>(undefined);

  // Function to fetch all advertising clients
  const fetchClients = useCallback(async () => {
    setLoading(true);
    try {
      const data = await advClientService.getAllAdvClients();
      setClients(data);
    } catch (error) {
      console.error('Error fetching advertising clients:', error);
      message.error(t('advertising.clients.notifications.error', { message: (error as Error).message }));
    } finally {
      setLoading(false);
    }
  }, [t]);

  // Load clients on component mount
  useEffect(() => {
    fetchClients();
  }, [fetchClients]);

  // Handle editing a client
  const handleEdit = (client: AdvClient) => {
    setSelectedClient(client);
    setFormVisible(true);
  };

  // Handle creating a new client
  const handleCreate = () => {
    setSelectedClient(undefined);
    setFormVisible(true);
  };

  // Handle deleting a client
  const handleDelete = async (id: number) => {
    try {
      await advClientService.deleteAdvClient(id);
      message.success(t('advertising.clients.notifications.delete_success'));
      fetchClients();
    } catch (error) {
      console.error('Error deleting advertising client:', error);
      message.error(t('advertising.clients.notifications.error', { message: (error as Error).message }));
    }
  };

  // After successful form submission, refresh the list
  const handleFormSuccess = () => {
    fetchClients();
  };

  // Table columns
  const columns = [
    {
      title: t('advertising.clients.list.name'),
      dataIndex: 'client_name',
      key: 'client_name',
    },
    {
      title: t('advertising.clients.list.status'),
      dataIndex: 'client_active',
      key: 'client_active',
      render: (active: boolean) => (
        <Tag color={active ? 'green' : 'red'}>
          {active ? t('advertising.clients.list.active') : t('advertising.clients.list.inactive')}
        </Tag>
      )
    },
    {
      title: t('advertising.clients.list.created'),
      dataIndex: 'created_at',
      key: 'created_at',
      render: (text: string) => new Date(text).toLocaleString()
    },
    {
      title: t('advertising.clients.list.updated'),
      dataIndex: 'updated_at',
      key: 'updated_at',
      render: (text: string) => new Date(text).toLocaleString()
    },
    {
      title: t('advertising.clients.list.actions'),
      key: 'actions',
      render: (_: any, record: AdvClient) => (
        <Space>
          <Button
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            size="small"
          >
            {t('advertising.clients.actions.edit')}
          </Button>
          <Popconfirm
            title={t('advertising.clients.actions.confirm_delete')}
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button icon={<DeleteOutlined />} danger size="small">
              {t('advertising.clients.actions.delete')}
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Card title={t('advertising.clients.list.title')}>
      <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div></div>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={handleCreate}
        >
          {t('advertising.clients.actions.create')}
        </Button>
      </div>

      <Table
        columns={columns}
        dataSource={clients}
        rowKey="id"
        loading={loading}
      />

      <AdvClientForm
        client={selectedClient}
        isVisible={formVisible}
        onClose={() => setFormVisible(false)}
        onSuccess={handleFormSuccess}
      />
    </Card>
  );
};

export default AdvClientsPage; 